import React, { useState, useEffect, useRef, useCallback } from 'react';
import { adminApiClient, Ticket } from '../lib/api';
import toast from 'react-hot-toast';
import { 
    X, 
    Send, 
    Paperclip, 
    User, 
    Shield, 
    Loader2, 
    FileText, 
    ExternalLink,
    Building,
    AlertCircle,
    CheckCircle2,
    LifeBuoy
} from 'lucide-react';

interface TicketDetailModalProps {
    ticketId: string;
    onClose: () => void;
}

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx'];

export const TicketDetailModalComponent: React.FC<TicketDetailModalProps> = ({ ticketId, onClose }) => {
    const [ticket, setTicket] = useState<Ticket | null>(null);
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState('');
    const [attachments, setAttachments] = useState<File[]>([]);
    const [sending, setSending] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const fetchTicket = useCallback(async () => {
        try {
            const res = await adminApiClient.getTicketById(ticketId);
            if (res.success && res.data) {
                setTicket(res.data.ticket || null);
            }
        } catch (error) {
            toast.error('Failed to load ticket details');
            onClose();
        } finally {
            setLoading(false);
        }
    }, [ticketId, onClose]);

    useEffect(() => {
        fetchTicket();
    }, [fetchTicket]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [ticket?.messages]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            const validFiles = newFiles.filter(file => {
                const ext = file.name.split('.').pop()?.toLowerCase() || '';
                if (!ALLOWED_EXTENSIONS.includes(ext)) {
                    toast.error(`"${file.name}" format not allowed.`);
                    return false;
                }
                if (file.size > MAX_FILE_SIZE) {
                    toast.error(`"${file.name}" is too large (> 1MB).`);
                    return false;
                }
                return true;
            });
            setAttachments([...attachments, ...validFiles]);
        }
    };

    const handleSendReply = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!reply.trim() && attachments.length === 0) return;

        setSending(true);
        try {
            const formData = new FormData();
            formData.append('message', reply);
            attachments.forEach(file => {
                formData.append('attachments', file);
            });

            const res = await adminApiClient.addTicketReply(ticketId, formData);
            if (res.success && res.data) {
                setReply('');
                setAttachments([]);
                setTicket(res.data.ticket || null);
                toast.success('Reply sent successfully');
            }
        } catch (error) {
            toast.error('Failed to send reply');
        } finally {
            setSending(false);
        }
    };

    const handleStatusUpdate = async (newStatus: string) => {
        setUpdatingStatus(true);
        try {
            const res = await adminApiClient.updateTicketStatus(ticketId, newStatus);
            if (res.success && res.data) {
                setTicket(res.data.ticket || null);
                toast.success(`Ticket status updated to ${newStatus.replace('_', ' ')}`);
            }
        } catch (error) {
            toast.error('Failed to update status');
        } finally {
            setUpdatingStatus(false);
        }
    };

    const getImageUrl = (url: string) => {
        const baseURL = (process as any).env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3002';
        return url.startsWith('http') ? url : `${baseURL}${url}`;
    };

    if (loading) return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-white p-8 rounded-2xl shadow-xl flex flex-col items-center gap-4">
                <Loader2 className="w-10 h-10 animate-spin text-orange-600" />
                <p className="text-gray-500 font-medium">Loading ticket details...</p>
            </div>
        </div>
    );

    if (!ticket) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-gray-50 rounded-3xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden animate-in zoom-in duration-200">
                {/* Header */}
                <div className="bg-white px-8 py-6 border-b border-gray-100 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-orange-100 rounded-2xl">
                            <LifeBuoy className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-1 rounded">{ticket.ticketId}</span>
                                <h2 className="text-xl font-bold text-gray-900">{ticket.subject}</h2>
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                                <span className="text-xs text-gray-400 flex items-center gap-1">
                                    <Building size={12} /> {(ticket.temple as any)?.name}
                                </span>
                                <span className="text-xs text-gray-400">• Raised by {(ticket.createdBy as any)?.name}</span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 flex overflow-hidden">
                    {/* Chat Area */}
                    <div className="flex-1 flex flex-col bg-white overflow-hidden border-r border-gray-100">
                        <div className="flex-1 overflow-y-auto p-8 space-y-6">
                            {/* Initial Message */}
                            <div className="flex gap-4">
                                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                                    <User className="w-5 h-5 text-orange-600" />
                                </div>
                                <div className="flex-1">
                                    <div className="bg-gray-50 p-5 rounded-2xl rounded-tl-none border border-gray-100">
                                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{ticket.description}</p>
                                        {ticket.attachments && ticket.attachments.length > 0 && (
                                            <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2">
                                                {ticket.attachments.map((att, idx) => (
                                                    <a key={idx} href={getImageUrl(att.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs bg-white border border-gray-200 px-3 py-1.5 rounded-lg hover:border-orange-300 transition-all">
                                                        <Paperclip size={12} />
                                                        <span className="max-w-[150px] truncate">{att.name}</span>
                                                        <ExternalLink size={12} className="opacity-50" />
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <span className="text-[10px] text-gray-400 mt-1 block">Initial Support Request • {new Date(ticket.createdAt).toLocaleString()}</span>
                                </div>
                            </div>

                            {/* Replies */}
                            {ticket.messages.map((msg, idx) => {
                                const isAdmin = msg.senderRole === 'super_admin';
                                return (
                                    <div key={idx} className={`flex gap-4 ${isAdmin ? 'flex-row-reverse' : ''}`}>
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isAdmin ? 'bg-indigo-100 text-indigo-600' : 'bg-orange-100 text-orange-600'}`}>
                                            {isAdmin ? <Shield size={18} /> : <User size={18} />}
                                        </div>
                                        <div className={`max-w-[80%] flex flex-col ${isAdmin ? 'items-end' : ''}`}>
                                            <div className={`p-4 rounded-2xl border ${isAdmin ? 'bg-indigo-50 border-indigo-100 rounded-tr-none text-indigo-900' : 'bg-orange-50 border-orange-100 rounded-tl-none text-orange-900'}`}>
                                                <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                                                {msg.attachments && msg.attachments.length > 0 && (
                                                    <div className="mt-3 pt-3 border-t border-current/10 flex flex-wrap gap-2">
                                                        {msg.attachments.map((att, attIdx) => (
                                                            <a key={attIdx} href={getImageUrl(att.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[10px] bg-white/50 px-2 py-1 rounded-md border border-current/5">
                                                                <Paperclip size={10} />
                                                                <span className="max-w-[120px] truncate">{att.name}</span>
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <span className="text-[10px] text-gray-400 mt-1">
                                                {isAdmin ? 'You (Support Team)' : (ticket.createdBy as any)?.name} • {new Date(msg.createdAt).toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Reply Input */}
                        <div className="p-6 bg-gray-50 border-t border-gray-100 shrink-0">
                            {ticket.status === 'closed' ? (
                                <div className="text-center p-4 bg-gray-200 rounded-2xl text-gray-600 font-medium text-sm">
                                    This ticket is closed and cannot be replied to.
                                </div>
                            ) : (
                                <form onSubmit={handleSendReply} className="space-y-4">
                                    {attachments.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {attachments.map((file, idx) => (
                                                <div key={idx} className="flex items-center gap-2 bg-white text-gray-600 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium">
                                                    <FileText size={12} />
                                                    <span className="max-w-[150px] truncate">{file.name}</span>
                                                    <button type="button" onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))} className="hover:text-red-500">
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="flex items-end gap-3">
                                        <div className="flex-1 bg-white rounded-2xl border border-gray-200 shadow-sm focus-within:ring-2 focus-within:ring-orange-500 transition-all overflow-hidden">
                                            <textarea
                                                value={reply}
                                                onChange={(e) => setReply(e.target.value)}
                                                placeholder="Type your reply here..."
                                                rows={2}
                                                className="w-full px-4 py-3 outline-none resize-none text-sm"
                                            />
                                            <div className="px-4 py-2 border-t border-gray-50 flex items-center justify-between bg-gray-50/50">
                                                <label className="p-1.5 hover:bg-gray-200 rounded-full cursor-pointer transition-colors text-gray-500">
                                                    <Paperclip size={18} />
                                                    <input type="file" multiple className="hidden" onChange={handleFileChange} />
                                                </label>
                                                <span className="text-[10px] text-gray-400">Temple admin will receive an email notification.</span>
                                            </div>
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={sending || (!reply.trim() && attachments.length === 0)}
                                            className="bg-orange-600 text-white p-4 rounded-2xl hover:bg-orange-700 transition-all shadow-lg active:scale-95 disabled:opacity-50"
                                        >
                                            {sending ? <Loader2 size={24} className="animate-spin" /> : <Send size={24} />}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>

                    {/* Sidebar Actions */}
                    <div className="w-80 shrink-0 p-8 space-y-8 bg-gray-50 overflow-y-auto">
                        <div>
                            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-4">Ticket Management</h3>
                            <div className="space-y-4">
                                <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2">Current Status</label>
                                        <div className="flex flex-wrap gap-2">
                                            {['open', 'in_progress', 'resolved', 'closed'].map(s => (
                                                <button
                                                    key={s}
                                                    onClick={() => handleStatusUpdate(s)}
                                                    disabled={updatingStatus || ticket.status === s}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                                                        ticket.status === s 
                                                        ? 'bg-orange-600 border-orange-600 text-white shadow-md' 
                                                        : 'bg-white border-gray-200 text-gray-600 hover:border-orange-300'
                                                    } disabled:opacity-50`}
                                                >
                                                    {s.replace('_', ' ').toUpperCase()}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="pt-4 border-t border-gray-50">
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2">Priority</label>
                                        <div className={`flex items-center gap-2 text-sm font-bold capitalize ${
                                            ticket.priority === 'urgent' ? 'text-red-600' :
                                            ticket.priority === 'high' ? 'text-orange-600' : 'text-blue-600'
                                        }`}>
                                            <AlertCircle size={16} />
                                            {ticket.priority} Priority
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-4">Temple Information</h3>
                            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                                <div>
                                    <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Temple Name</p>
                                    <p className="text-sm font-bold text-gray-900">{(ticket.temple as any)?.name}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Admin Contact</p>
                                    <p className="text-sm font-medium text-gray-800">{(ticket.createdBy as any)?.name}</p>
                                    <p className="text-xs text-gray-500">{(ticket.createdBy as any)?.email}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 flex gap-3">
                            <CheckCircle2 size={18} className="text-blue-600 shrink-0" />
                            <p className="text-xs text-blue-800 leading-relaxed">
                                Updating ticket status or replying will trigger an automated email notification to the temple admin.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TicketDetailModalComponent;
